import { useLocation } from 'react-router';

import type { FeatureFlag } from './createFeatureFlagsContext';
import type { DistributedOmit } from './types';

/**
 * useSearchParamsToFeatureFlags is a hook that reads the feature flags from the URL search params.
 *
 * The search param may look like this:
 * &ff=feature1:variant-1,feature2:variant-2,feature3:variant-3
 *
 * It only reads the feature flags that are passed in the `featureKeys` array, which is created when the FeatureFlags Config is created.
 * It doesn't do any feature variant checking, and will forcefully set the variant.
 *
 * This is intentionally because:
 * - variants can change anytime, so we don't want to check variant types
 * - variatns from URL search params are only used for local development and testing
 */
export const useSearchParamsToFeatureFlags = <
  F extends FeatureFlag<K, V>,
  K extends string = F['key'],
  V extends string = F['variant'],
>(
  featureKeys: Array<F['key']>,
): Array<DistributedOmit<F, 'reason'>> => {
  const searchKey = 'ff';

  const { search } = useLocation();

  const urlSearchParams = new URLSearchParams(search);

  // featureFlags may look like this: 'ff=feature1:variant-1,feature2:variant-2,feature3:variant-3'
  const featureFlags = urlSearchParams.get(searchKey);

  if (!featureFlags) {
    return [];
  }

  const result: Array<DistributedOmit<F, 'reason'>> = [];

  featureFlags.split(',').map((featureFlag) => {
    const [key, variant] = featureFlag.split(':');

    const feature = featureKeys.find((featureKey) => featureKey === key);

    if (feature) {
      result.push({
        key: feature,
        variant,
      } as DistributedOmit<F, 'reason'>); // Note: this `as` is slightly dangerous because we are assuming the variant input in the URL matches the typed variant. However, this is only used for local development and testing, so we can accept the risk.
    }
  });

  return result;
};
